import React from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Image,
  Dimensions,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../hooks/useTheme';
import DetailContainer from '../../components/shared/DetailContainer';
import Text from '../../components/common/Text';
import { Button } from '../../components';
import { spacing, radii, shadows, fontSize, fontWeight } from '../../themes';

type ColorAnalysisDetailNavigationProp =
  NativeStackNavigationProp<RootStackParamList>;

const { width: screenWidth } = Dimensions.get('window');

const ColorAnalysisDetailScreen = () => {
  const navigation = useNavigation<ColorAnalysisDetailNavigationProp>();
  const { colors } = useTheme();

  const handleStartAnalysis = () => {
    navigation.navigate('MainTabs', { initialTab: 'ColourAnalysis' });
  };

  const keyFeatures = [
    {
      icon: 'camera-outline',
      title: 'Smart Photo Analysis',
      description:
        'Our AI analyzes your facial features, skin undertones, and natural coloring from a simple selfie',
    },
    {
      icon: 'palette-outline',
      title: 'Personalized Colour Palette',
      description:
        'Get a custom colour palette with 20+ colors that complement your unique features',
    },
    {
      icon: 'sunny-outline',
      title: 'Season Classification',
      description:
        "Discover if you're a Spring, Summer, Autumn, or Winter and what that means for your style",
    },
    {
      icon: 'checkmark-circle-outline',
      title: 'Colour Confidence',
      description:
        'Never second-guess your colour choices again with science-backed recommendations',
    },
    {
      icon: 'trending-up-outline',
      title: 'Style Enhancement',
      description:
        'Learn how to enhance your natural beauty with the right colors for makeup and wardrobe',
    },
    {
      icon: 'heart-outline',
      title: 'Instant Results',
      description:
        'Get your complete colour analysis in under 60 seconds with detailed explanations',
    },
  ];

  const steps = [
    {
      step: '01',
      title: 'Take a Selfie',
      description: 'Capture a clear photo in natural lighting',
    },
    {
      step: '02',
      title: 'AI Analysis',
      description: 'Our advanced AI analyzes your features',
    },
    {
      step: '03',
      title: 'Get Results',
      description: 'Receive your personalized colour palette',
    },
  ];

  return (
    <DetailContainer style={{ backgroundColor: colors.background }}>
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        {/* Hero Section */}
        <LinearGradient
          colors={[colors.theme_color, colors.primary]}
          style={styles.heroSection}
        >
          <View style={styles.heroContent}>
            <Text
              size="3xl"
              weight="extrabold"
              align="center"
              style={[styles.heroTitle, { color: colors.background }]}
            >
              Discover Your Perfect Colors with AI
            </Text>
            <Text size="md" align="center" style={styles.heroSubtitle}>
              Get a personalized colour analysis that reveals which colors make
              you look radiant and confident
            </Text>
          </View>
        </LinearGradient>

        {/* Product Demo Images */}
        <View style={styles.demoSection}>
          <Text
            size="2xl"
            weight="bold"
            align="center"
            style={[styles.sectionTitle, { color: colors.text }]}
          >
            See It In Action
          </Text>
          <View style={styles.demoGrid}>
            <View style={styles.demoImageContainer}>
              <Image
                source={{ uri: 'https://picsum.photos/300/500?random=1' }}
                style={styles.demoImage}
                resizeMode="cover"
              />
              <Text size="sm" weight="medium" style={{ color: colors.text }}>
                Before Analysis
              </Text>
            </View>
            <View style={styles.demoImageContainer}>
              <Image
                source={{ uri: 'https://picsum.photos/300/500?random=2' }}
                style={styles.demoImage}
                resizeMode="cover"
              />
              <Text size="sm" weight="medium" style={{ color: colors.text }}>
                AI Processing
              </Text>
            </View>
            <View style={styles.demoImageContainer}>
              <Image
                source={{ uri: 'https://picsum.photos/300/500?random=3' }}
                style={styles.demoImage}
                resizeMode="cover"
              />
              <Text size="sm" weight="medium" style={{ color: colors.text }}>
                Your Colour Palette
              </Text>
            </View>
          </View>
        </View>

        {/* How It Works */}
        <View style={styles.section}>
          <Text
            size="2xl"
            weight="bold"
            align="center"
            style={[styles.sectionTitle, { color: colors.text }]}
          >
            How It Works
          </Text>
          <View style={styles.stepsContainer}>
            {steps.map((step, index) => (
              <View key={index} style={styles.stepItem}>
                <View
                  style={[
                    styles.stepNumber,
                    { backgroundColor: colors.theme_color },
                  ]}
                >
                  <Text
                    size="md"
                    weight="semibold"
                    style={{ color: colors.background }}
                  >
                    {step.step}
                  </Text>
                </View>
                <View style={styles.stepContent}>
                  <Text
                    size="lg"
                    weight="semibold"
                    style={[styles.stepTitle, { color: colors.text }]}
                  >
                    {step.title}
                  </Text>
                  <Text
                    size="sm"
                    style={{ color: colors.secondary, lineHeight: 20 }}
                  >
                    {step.description}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Key Features */}
        <View
          style={[
            styles.section,
            Platform.OS !== 'web' && styles.sectionMobile,
          ]}
        >
          <Text
            size="2xl"
            weight="bold"
            align="center"
            style={[styles.sectionTitle, { color: colors.text }]}
          >
            Key Features
          </Text>
          <View style={styles.featuresGrid}>
            {keyFeatures.map((feature, index) => (
              <View
                key={index}
                style={[
                  styles.featureCard,
                  {
                    backgroundColor: colors.background,
                    shadowColor: colors.text,
                  },
                ]}
              >
                <View style={styles.featureIcon}>
                  <Ionicons
                    name={feature.icon as keyof typeof Ionicons.glyphMap}
                    size={24}
                    color={colors.theme_color}
                  />
                </View>
                <Text
                  size="sm"
                  weight="semibold"
                  align="center"
                  style={[styles.featureTitle, { color: colors.text }]}
                >
                  {feature.title}
                </Text>
                <Text
                  size="xs"
                  align="center"
                  style={{ color: colors.secondary, lineHeight: 16 }}
                >
                  {feature.description}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* Example Results */}
        <View
          style={[
            styles.section,
            Platform.OS !== 'web' && styles.sectionMobile,
          ]}
        >
          <Text
            size="2xl"
            weight="bold"
            align="center"
            style={[styles.sectionTitle, { color: colors.text }]}
          >
            What You&apos;ll Get
          </Text>
          <View
            style={[
              styles.resultCard,
              { backgroundColor: colors.card, shadowColor: colors.text },
            ]}
          >
            <Image
              source={{ uri: 'https://picsum.photos/400/200?random=4' }}
              style={styles.resultImage}
              resizeMode="cover"
            />
            <View style={styles.resultContent}>
              <Text
                size="xl"
                weight="semibold"
                style={[styles.resultTitle, { color: colors.text }]}
              >
                Your Complete Colour Profile
              </Text>
              <View style={styles.resultFeatures}>
                {[
                  '20+ personalized colors',
                  'Season classification',
                  'Style recommendations',
                  'Shopping guide',
                ].map((item) => (
                  <View key={item} style={styles.resultFeature}>
                    <Ionicons
                      name="checkmark-circle"
                      size={16}
                      color={colors.success}
                    />
                    <Text
                      size="sm"
                      style={[styles.resultFeatureText, { color: colors.text }]}
                    >
                      {item}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        </View>

        {/* CTA Section */}
        <View style={styles.ctaSection}>
          <LinearGradient
            colors={[colors.theme_color, colors.primary]}
            style={styles.ctaGradient}
          >
            <Text
              size="2xl"
              weight="bold"
              align="center"
              style={[styles.ctaTitle, { color: colors.background }]}
            >
              Ready to Discover Your Colors?
            </Text>
            <Text size="md" align="center" style={styles.ctaSubtitle}>
              Join thousands who&apos;ve transformed their style
            </Text>
            <Button
              title="Start Colour Analysis"
              onPress={handleStartAnalysis}
              variant="primary"
              style={{
                ...styles.ctaButton,
                backgroundColor: colors.background,
              }}
              textStyle={{ ...styles.ctaButtonText, color: colors.theme_color }}
              hoverColor={colors.border}
              trackClick={true}
              trackingLocation="color-analysis-detail-screen"
              trackingData={{
                button_name: 'Start Colour Analysis Detail Screen',
                button_type: 'start_colour_analysis_detail_screen',
                screen: 'color_analysis_detail',
              }}
            />
          </LinearGradient>
        </View>
      </ScrollView>
    </DetailContainer>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  heroSection: {
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[10],
    alignItems: 'center',
  },
  heroContent: {
    maxWidth: 500,
    alignItems: 'center',
  },
  heroTitle: {
    marginBottom: spacing[4],
    fontSize: fontSize['3xl'],
    fontWeight: fontWeight.extrabold,
  },
  heroSubtitle: {
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: 24,
  },
  demoSection: {
    paddingVertical: spacing[7],
  },
  demoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: spacing[4],
  },
  demoImageContainer: {
    alignItems: 'center',
    width: (screenWidth - 60) / 2,
    maxWidth: 160,
  },
  demoImage: {
    width: '100%',
    height: 300,
    borderRadius: radii.lg,
    marginBottom: spacing[2],
  },
  section: {
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[7],
  },
  sectionMobile: {
    paddingHorizontal: spacing[3],
  },
  sectionTitle: {
    marginBottom: spacing[6],
  },
  stepsContainer: {
    gap: spacing[5],
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  stepNumber: {
    width: 40,
    height: 40,
    borderRadius: radii.full,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing[4],
    flexShrink: 0,
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    marginBottom: spacing[1],
  },
  featuresGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[4],
    justifyContent: 'center',
  },
  featureCard: {
    width: (screenWidth - 60) / 2,
    maxWidth: 160,
    padding: spacing[4],
    borderRadius: radii.lg,
    alignItems: 'center',
    ...shadows.md,
  },
  featureIcon: {
    width: 48,
    height: 48,
    borderRadius: radii.full,
    backgroundColor: 'rgba(139, 92, 246, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing[3],
  },
  featureTitle: {
    marginBottom: spacing[2],
  },
  resultCard: {
    borderRadius: radii.xl,
    overflow: 'hidden',
    ...shadows.lg,
  },
  resultImage: {
    width: '100%',
    height: 200,
  },
  resultContent: {
    padding: spacing[5],
  },
  resultTitle: {
    marginBottom: spacing[4],
  },
  resultFeatures: {
    gap: spacing[2],
  },
  resultFeature: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  resultFeatureText: {
    marginLeft: spacing[3],
  },
  ctaSection: {
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[7],
  },
  ctaGradient: {
    borderRadius: radii.xl,
    paddingVertical: spacing[10],
    paddingHorizontal: spacing[5],
    alignItems: 'center',
  },
  ctaTitle: {
    marginBottom: spacing[2],
  },
  ctaSubtitle: {
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: spacing[6],
  },
  ctaButton: {
    paddingVertical: spacing[4],
    paddingHorizontal: spacing[8],
    borderRadius: radii.lg,
  },
  ctaButtonText: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
  },
});

export default ColorAnalysisDetailScreen;
